/* =====================================================================
   tower/driver/src/nudges.js — "time to move" nudges on the handheld (07-ACCEPTANCE F12; Parik 2026-10-22).

   The rules are nudge-rules.js (pure, tested on simulated tracks). This file only connects them to the phone:
     • GPS: piggybacks on the fixes the app ALREADY takes (feature.js's telemetry watch calls drvNudgeFix) — no new
       polling, no new permission prompt; a slow 20 s timer re-checks so time passing counts while standing still
       (after Start, before Done, stopped, screen on);
     • the shift: read through window._drvNudgeSrc (feature.js) — stop states, the next stop, the stop open on screen,
       Start / Done; the route's warehouse (`yard`) so loading beside the first customer is not "still at";
     • the company's setting: STATE.config.driverNudges (/api/driver/me on a phone, Settings → Preferences in the
       office); planned minutes per stop: the route's `dwell` (the location's minutes on site), else the company's;
     • the cue: one compact card at the top (≤ 72 px; role=status, aria-live=polite) — the words (tap for all of
       them) and what to do: Go / Open / Skip, Snooze, Dismiss — a gentle vibration, and a line in the alert strip.
   All on the device: it works offline. Only on the driver app (?app=driver) — never in the office's preview.
   Arrivals at stops and what was said are kept for the day on this phone, per driver, so a reload repeats nothing.
   ===================================================================== */
import { t, fmtTimeL, onLocale } from './i18n.js';
import * as R from './nudge-rules.js';

window.DrvNudgeRules = R;   // the office's Radar (nudges-office.js) and Test Assist run the SAME rules

const IS_APP = (() => { try { return new URLSearchParams(location.search).get('app') === 'driver'; } catch (e) { return false; } })();
const KEY = 'routingapp:driver:nudges:v2:';   // + company:driver — one phone, several drivers, never mixed
let mem = R.newMem(), memDay = '', memKey = '', showing = null, shownCue = null, timer = null, savedAt = 0, expanded = false;

const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const cfg = () => R.normConfig(((typeof STATE !== 'undefined' && STATE.config) || {}).driverNudges);
function today() { try { return typeof todayISO === 'function' ? todayISO() : new Date().toDateString(); } catch (e) { return ''; } }
/** The driver this phone is working as — an impersonating tab (office support) runs as ITS target driver, whose id is
 *  only in the session token, so the token is read when the stored record has no id. */
function tokenDriver() {
  try {
    const imp = JSON.parse(sessionStorage.getItem('routingapp:driver:imp') || 'null');
    const tok = (imp && imp.token) || localStorage.getItem('routingapp:driver:token') || '';
    const p = JSON.parse(atob(String(tok).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { id: p['routingapp:driver_id'] || p.sub || '', tenant: p['routingapp:tenant_id'] || '', imp: !!(imp && imp.token) };
  } catch (e) { return { id: '', tenant: '', imp: false }; }
}
function owner() {
  try {
    const w = (window.DRIVER_SESSION && window.DRIVER_SESSION.who) || JSON.parse(localStorage.getItem('routingapp:driver:who') || 'null') || {};
    const tk = tokenDriver();
    const id = tk.imp ? (tk.id || w.id) : (w.id || tk.id);
    return (w.tenant || tk.tenant || '-') + ':' + (id || '-');
  } catch (e) { return '-:-'; }
}

/* the day's memory — arrivals at stops and what was said — kept on this phone for THIS driver; a new day starts clean */
function load() {
  const d = today(), k = KEY + owner();
  if (memDay === d && memKey === k) return;
  memDay = d; memKey = k; mem = R.newMem(); showing = null; shownCue = null;
  try { const s = JSON.parse(localStorage.getItem(k) || 'null'); if (s && s.day === d) R.restore(mem, s); } catch (e) {}
  try {   // other days' memories go (another driver's today stays theirs)
    Object.keys(localStorage).forEach((x) => {
      if (x.indexOf('routingapp:driver:nudges:') !== 0 || x === k) return;
      let o = null; try { o = JSON.parse(localStorage.getItem(x) || 'null'); } catch (e) {}
      if (!o || o.day !== d) localStorage.removeItem(x);
    });
  } catch (e) {}
}
function save() { savedAt = Date.now(); try { localStorage.setItem(memKey, JSON.stringify(Object.assign({ day: memDay }, R.persist(mem)))); } catch (e) {} }

function src() { return typeof window._drvNudgeSrc === 'function' ? window._drvNudgeSrc() : null; }
/* the stops as the rules read them */
function input(sr) {
  const r = sr.r, dwell = r.dwell || {};
  const stops = r.stops.map((s, i) => {
    const st = sr.stat(i), w = sr.win(i), ll = s[1];
    const svc = dwell[s[7]];
    return { key: sr.key(i), name: s[0], ll: (Array.isArray(ll) && isFinite(ll[0]) && isFinite(ll[1]) && (ll[0] || ll[1])) ? ll : null,
      state: st ? (st.st === 'skipped' ? 'skipped' : 'done') : 'open', doneAt: st && st.when ? st.when : null,
      serviceMin: svc != null && isFinite(svc) && svc > 0 ? Number(svc) : null, winEnd: w && isFinite(w.b) ? w.b : null };
  });
  let nowMin = null; try { nowMin = typeof companyNowMin === 'function' ? companyNowMin() : null; } catch (e) {}
  const y = r.yard && isFinite(r.yard.lat) && isFinite(r.yard.lng) ? [Number(r.yard.lat), Number(r.yard.lng)] : null;
  return { now: Date.now(), nowMin, stops, cur: sr.cur, opened: sr.opened != null ? sr.opened : -1, yard: y };
}

/* ── the card: the words (≤ 3 lines, tap for all of them) + the actions, ≤ 72 px ─────────────────────── */
/** A company wall minute (the window's end) in the driver's words: "11:30 AM" / "a las 11:30 a. m." / "a la 1:00 p. m." */
function winTime(min) {
  if (min == null || !isFinite(min)) return '';
  const hhmm = String(Math.floor(min / 60) % 24).padStart(2, '0') + ':' + String(Math.round(min) % 60).padStart(2, '0');
  const f = fmtTimeL(typeof t12 === 'function' ? t12(hhmm) : hhmm);
  return t(/^1[:.]/.test(f) ? 'dh.nudge.at1' : 'dh.nudge.at', { time: f });
}
function words(c) {
  const time = winTime(c.winEnd);
  if (c.kind === 'linger') return { title: t('dh.nudge.linger.title'), body: c.winEnd != null ? t('dh.nudge.linger', { next: c.next, min: c.drive, time }) : t('dh.nudge.lingerNoWin', { next: c.next, min: c.drive }) };
  if (c.kind === 'risk') return { title: t('dh.nudge.risk.title'), body: t('dh.nudge.risk', { next: c.next, min: c.drive, time }) };
  if (c.kind === 'long') return { title: t('dh.nudge.long.title'), body: t('dh.nudge.long', { name: c.name }) };
  return { title: t('dh.nudge.left.title'), body: t('dh.nudge.left', { name: c.name }) };
}
function region() {
  let el = document.getElementById('drvNudge');
  if (!el) {
    el = document.createElement('div'); el.id = 'drvNudge';
    el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite');   // polite: never interrupts the driver mid-sentence
    document.body.appendChild(el);
  }
  el.setAttribute('aria-label', t('dh.nudge.region'));
  return el;
}
const btn = (act, label, cls) => `<button type="button" class="nd-b${cls ? ' ' + cls : ''}" data-act="${act}" onclick="drvNudgeAct('${act}')">${esc(label)}</button>`;
const ico = (act, icon, label) => `<button type="button" class="nd-i" data-act="${act}" aria-label="${esc(label)}" title="${esc(label)}" onclick="drvNudgeAct('${act}')">${icon}</button>`;
/* just BELOW the screen's top strip (Back, sync chip, EN/ES, menu stay uncovered) — measured, every layout */
function place(el) {
  try {
    const top = document.querySelector('#mod-driverapp .dh-top');
    const r = top && top.getClientRects().length ? top.getBoundingClientRect() : null;
    el.style.top = r && r.bottom > 0 ? Math.round(r.bottom + 4) + 'px' : '';
  } catch (e) {}
}
function paint() {
  const el = region(), c = shownCue;
  if (!c) { if (el.firstChild) el.innerHTML = ''; return; }
  place(el);
  const w = words(c);
  const acts = (c.kind === 'left' ? btn('nudgeOpen', t('dh.nudge.open'), 'pri') + btn('nudgeSkip', t('dh.nudge.skip'))
    : c.kind === 'long' ? btn('nudgeOpen', t('dh.nudge.open'), 'pri') : btn('nudgeGo', t('dh.nudge.go'), 'pri'))
    + ico('nudgeSnooze', '⏰', t('dh.nudge.snooze', { min: cfg().snoozeMin })) + ico('nudgeDismiss', '✕', t('dh.nudge.dismiss'));
  el.innerHTML = `<div class="nd nd-${c.kind}${expanded ? ' open' : ''}" data-nudge="${c.kind}" aria-label="${esc(w.title + ' — ' + w.body)}">`
    + `<button type="button" class="nd-m" data-act="nudgeMore" aria-expanded="${expanded}" title="${esc(t('dh.nudge.more'))}" onclick="drvNudgeMore()">${esc(w.body)}</button>`
    + `<div class="nd-a">${acts}</div></div>`;
}
window.drvNudgeMore = () => { expanded = !expanded; paint(); };
function announce(c) {
  try { if (navigator.vibrate) navigator.vibrate(c.kind === 'risk' || c.kind === 'left' ? [90, 60, 90, 60, 90] : [40]); } catch (e) {}
  // one line in the alert strip, stored as a key so a language switch re-words it (live handheld only)
  try {
    if (typeof window.drvAlert === 'function') {
      const k = c.kind === 'linger' ? (c.winEnd != null ? 'dh.nudge.linger' : 'dh.nudge.lingerNoWin') : 'dh.nudge.' + c.kind;
      window.drvAlert('nudge', { key: k, params: { next: c.next || '', name: c.name || '', min: c.drive || 0, time: winTime(c.winEnd) } });
    }
  } catch (e) {}
}

/* ── evaluate ──────────────────────────────────────────────────────────────────────────────────── */
function tick() {
  if (!IS_APP) return null;
  load();
  const sr = src(), C = cfg();
  if (!sr || !sr.r || !Array.isArray(sr.r.stops)) { shownCue = null; showing = null; paint(); return null; }
  const inp = input(sr);
  const res = R.conditions(inp, mem, C);
  const was = showing;
  const c = R.pick(res, mem, inp.now, { on: C.on, started: sr.started, ended: sr.ended }, showing);
  showing = c ? R.cueId(c) : null;
  // while driving, a "still at" / "you left" card already up stays (it is still true); a new cue waits for a stop
  if (res.gps && res.driving && was && !c && res.list.some((x) => R.cueId(x) === was) && !/^(linger|risk)\|/.test(was)) { showing = was; return shownCue; }
  if (c && R.cueId(c) !== was) { shownCue = c; expanded = false; save(); paint(); announce(c); }
  else if (!c && shownCue) { shownCue = null; save(); paint(); }
  else if (c) { shownCue = c; place(region()); }
  return shownCue;
}
/** A fix from the app's own GPS watch (feature.js). Fixes are kept; the rules decide what they mean. */
window.drvNudgeFix = (lat, lng, mph, acc) => {
  if (!IS_APP) return;
  load();
  const sr = src(), before = JSON.stringify(mem.at);
  R.track(mem, { lat, lng, at: Date.now(), mph, acc }, sr && sr.r ? input(sr).stops : [], cfg());
  // arrivals survive a reload: saved when one changes, and at most every 15 s otherwise
  if (Date.now() - savedAt > 15000 || JSON.stringify(mem.at).length !== before.length) save();
  tick();
};
window.drvNudgeTick = tick;
window.drvNudgeAct = (act) => {
  const c = shownCue; if (!c) return;
  const id = R.cueId(c), now = Date.now();
  if (act === 'nudgeSnooze') R.snooze(mem, id, now, cfg());
  else R.dismiss(mem, id);              // every other answer settles it for this stop
  shownCue = null; showing = null; expanded = false; save(); paint();
  try {
    if (act === 'nudgeOpen' && typeof window.drvStopOpen === 'function') window.drvStopOpen(c.idx);
    else if (act === 'nudgeSkip' && typeof window.drvSkipMenu === 'function') window.drvSkipMenu(c.idx);
    else if (act === 'nudgeGo') {
      const sr = src(), s = sr && sr.r && sr.r.stops[c.idx];
      if (s && s[1] && typeof window.drvNav === 'function') window.drvNav(s[1][0], s[1][1]);
    }
  } catch (e) {}
};
onLocale(() => { try { paint(); } catch (e) {} });
if (IS_APP) window.addEventListener('resize', () => { if (shownCue) place(region()); });

/* the slow re-check: time passing counts while the truck stands still. Only after Start (and before Done), only while
   stopped, only with the screen on — and it never asks for GPS. */
window.drvNudgeTimerTick = () => {
  if (!IS_APP || document.hidden) return;
  const sr = src(); if (!sr || !sr.started || sr.ended) return;
  const f = mem.fixes[mem.fixes.length - 1], C = cfg();
  if (!C.on || (f && f.mph != null && f.mph > C.drivingMph)) return;
  window.__drvNudgeEvaluations = (window.__drvNudgeEvaluations || 0) + 1;
  try { tick(); } catch (e) {}
};
if (IS_APP && !timer) timer = setInterval(window.drvNudgeTimerTick, 20000);

/* the card's look: the handheld's theme tokens (--dh-*); compact (≤ 72 px) at the top, clear of the action bar and the strip */
// i18n-exempt-start (a stylesheet, not words)
if (IS_APP && !document.getElementById('drvNudgeCss')) {
  const st = document.createElement('style'); st.id = 'drvNudgeCss';
  st.textContent = '#drvNudge{position:fixed;left:6px;right:6px;top:calc(env(safe-area-inset-top,0px) + 4px);z-index:2340;pointer-events:none}'
    + '#drvNudge .nd{pointer-events:auto;max-width:560px;margin:0 auto;box-sizing:border-box;display:flex;align-items:center;gap:6px;background:var(--dh-surf,#fff);color:var(--dh-ink,#111);'
    + 'border:2px solid var(--dh-line,#111);border-radius:12px;padding:4px 4px 4px 10px;box-shadow:0 6px 18px rgba(0,0,0,.28);max-height:72px}'
    + '#drvNudge .nd.open{max-height:none;align-items:flex-start}'
    + '#drvNudge .nd-risk,#drvNudge .nd-left{background:var(--dh-ab,#FFF1D4);color:var(--dh-at,#6B3F00);border-color:var(--dh-al,#8A5300)}'
    + '#drvNudge .nd-m{flex:1;min-width:0;border:0;background:transparent;color:inherit;font:inherit;font-size:13.5px;line-height:17px;font-weight:600;text-align:left;padding:0;cursor:pointer;'
    + 'display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;overflow:hidden;overflow-wrap:anywhere}'
    + '#drvNudge .nd.open .nd-m{display:block}'
    + '#drvNudge .nd-a{display:flex;gap:4px;align-items:center;flex:none}'
    + '#drvNudge .nd-b{min-height:44px;padding:0 10px;border-radius:9px;border:2px solid currentColor;background:transparent;color:inherit;font:inherit;font-size:14px;font-weight:700;cursor:pointer}'
    + '#drvNudge .nd-b.pri{background:var(--dh-acc,#0A6B66);color:var(--dh-on,#fff);border-color:var(--dh-acc,#0A6B66)}'
    + '#drvNudge .nd-i{min-width:36px;min-height:44px;padding:0;border:0;background:transparent;color:inherit;font-size:17px;cursor:pointer}';
  document.head.appendChild(st);
}
// i18n-exempt-end
